<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import type { CanvasEx } from '@/typings/canvas';
import type { ImageColor, ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { useColor } from '@/composables/color';

const COLOR_TYPE = LibraryTypeEnum.Color;

const props = defineProps<{
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
    cmykMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'open-color-library-selector'): void;
}>();

const model = reactive({
    colors: [] as ImageColor[],
    colorOption: 0,
    colorMap: new Map<number, ColorObject>(),
    colorLibraryId: undefined as number | undefined
});

const disabled = computed(() => !!props.imageElement?.locked);

watch(
    () => props.imageElement,
    (element) => {
        if (!element) return;

        model.colorOption = element.selectedColorPosition || 1;
        model.colorLibraryId = element.colorLibraryId;
        initColorMap(element.colors || []);
    },
    { immediate: true }
);

const initColorMap = (colors: ImageColor[]) => {
    model.colors = [...colors];
    model.colorMap = new Map(model.colors.map((v) => [v.position, v]));
};

const onColorSelected = (color: ElementMapOption, option = 1) => {
    color = color as ColorObject;
    const element = props.imageElement;
    if (!element) return;

    setEditedProp('colors');

    if (!element.colors) {
        element.colors = [];
    }

    const existingColor = element.colors.find((v) => v.position === option);

    if (existingColor) {
        existingColor.hex = color.hex;
        existingColor.alpha = color.alpha;
        existingColor.cmyk = color.cmyk;
    } else {
        element.colors.push({ ...color, position: option });
    }

    initColorMap(element.colors);
    loadColorIntoElement(option);
};

const previewColorOption = (color?: ElementMapOption) => {
    loadColorIntoElement(model.colorOption || 1, color);
};

const { hexToRgba } = useColor();
const loadColorIntoElement = async (option: number, value?: ElementMapOption) => {
    const element = props.imageElement;
    if (!element) {
        return;
    }

    const DEFAULT_OPTION = 1;
    let color = (value as ColorObject) || model.colorMap.get(option) || model.colorMap.get(DEFAULT_OPTION);

    // Fallback for images created before multiple colors were supported:
    // use the legacy `color` property if no mapped colors are available.
    if (!color) {
        const legacyColor = (element as any).color as string | undefined;
        if (!legacyColor) {
            element.selectedColorPosition = model.colorOption = DEFAULT_OPTION;
            return;
        }

        element.selectedColorPosition = model.colorOption = DEFAULT_OPTION;
        const rgba = hexToRgba(legacyColor, 1);
        element.set('imageColor', rgba);
        props.canvas.requestRenderAll();
        return;
    }

    // If we resolved the color via fallback to DEFAULT_OPTION, keep the option state in sync.
    const appliedOption = model.colorMap.has(option) ? option : DEFAULT_OPTION;
    element.selectedColorPosition = model.colorOption = appliedOption;
    const rgba = color.alpha < 1 ? hexToRgba(color.hex, color.alpha) : color.hex;
    element.set('imageColor', rgba);
    props.canvas.requestRenderAll();
};

const removeOptionFromMap = (position: number) => {
    const element = props.imageElement;
    if (!element) return;

    element.colors = (element.colors || []).filter((v) => v.position !== position);
    initColorMap(element.colors);
    setEditedProp('colors');

    loadColorIntoElement(1);
};

const onOptionsReordered = (options: KeyValueDto<number, ElementMapOption>[]) => {
    const element = props.imageElement;
    if (!element) return;

    const colors: ImageColor[] = [];
    let newPosition = 1;
    const userColors = new Map(model.colors.map((v) => [v.position, v]));

    options.forEach(({ id }, index) => {
        const color = userColors.get(id!)!;

        colors.push({ ...color, position: index + 1 });

        if (id === model.colorOption) {
            newPosition = index + 1;
        }
    });

    element.colors = [...colors];

    setEditedProp('colors');
    initColorMap(colors);
    loadColorIntoElement(newPosition);
};

const pickColorLibrary = () => {
    const element = props.imageElement;

    element.selectedColorPosition = model.colorOption = 1;
    emit('open-color-library-selector');
};

const removeColorLibrary = () => {
    const element = props.imageElement;
    if (!element) return;

    setEditedProp('colorLibraryId');
    element.colorLibraryId = undefined;
    model.colorLibraryId = undefined;
    loadColorIntoElement(1);
};

const setEditedProp = (prop: string) => {
    const element = props.imageElement;
    if (!element) return;

    if (props.editMode) {
        element.setEditedProp(prop);
    }
};
</script>

<template>
    <div class="image-color-properties-wrapper">
        <element-library-selector
            class="selector-wrapper"
            :disabled="disabled"
            :element-list="model.colorMap"
            :library-id="model.colorLibraryId"
            :library-type="COLOR_TYPE"
            :selected-position="model.colorOption"
            :cmyk="cmykMode"
            @option-selected="onColorSelected"
            @load-option="loadColorIntoElement"
            @options-reordered="onOptionsReordered"
            @remove-map-option="removeOptionFromMap"
            @remove-library="removeColorLibrary"
            @pick-library="pickColorLibrary"
            @preview-selection="previewColorOption"
        />
    </div>
</template>
